import { ArticleContentBlock } from "~/components/ArticleContentBlock";
import { CorrectionBlockCard } from "~/components/CorrectionBlockCard";
import { EvervizChart } from "~/components/EvervizChart";
import { apiChartToEvervizChartProps } from "~/components/EvervizChart/apiChartToEvervizChartProps";
import { apiDatavizToEvervizChartProps } from "~/components/EvervizChart/apiDatavizToEvervizChartProps";
import {
  TabbedChartTab,
  TabbedCharts,
} from "~/components/TabbedCharts/TabbedCharts";
import { useTrackerNavigationItemRef } from "~/components/TrackerPage/TrackerNavigationProvider";
import {
  TrackerContentBlock as ContentBlockType,
  TrackerPlainContentBlock,
} from "~/types/asgard/Tracker";
import { Body, Headline } from "~/ui-library";
import { OptionData } from "~/ui-library/components/Select";

import { LandingPageContent } from "~/components/LandingPageV2/components/LandingPageContent";
import {
  isCorrectionBlockContentBlock,
  isDatavizBlockContentBlock,
  isInlineChartBlockContentBlock,
  isPlainContentBlockContentBlock,
  isTabbedChartContentBlock,
} from "./tracker-content-block-type-guards";

export type TrackerContentBlockProps = {
  contentBlock: ContentBlockType;
  maxContentWidth: "medium" | "memo";
};

const PlainContentBlock = ({
  contentBlock,
}: {
  contentBlock: TrackerPlainContentBlock;
}) => (
  <div>
    <Headline
      as="h2"
      className="mb-m scroll-mt-xxl"
      level={3}
      mdLevel={2}
      id={contentBlock.blockId}
    >
      {contentBlock.headline}
    </Headline>
    <Body size="large">
      <ArticleContentBlock contentHTML={contentBlock.body} />
    </Body>
  </div>
);

export const TrackerContentBlock: React.FC<TrackerContentBlockProps> = ({
  contentBlock,
  maxContentWidth,
}) => {
  const datavizContainerRef = useTrackerNavigationItemRef(
    contentBlock.blockId,
    {
      /** charts are typically much larger than the other content blocks, so
       * decrease threshold when highlighting in the nav
       */
      threshold: 0.6,
    }
  );

  const textContainerRef = useTrackerNavigationItemRef(contentBlock.blockId, {
    threshold: 1,
  });

  const tabbedChartContaineref = useTrackerNavigationItemRef(
    contentBlock.blockId,
    {
      threshold: 0.6,
    }
  );

  if (isInlineChartBlockContentBlock(contentBlock)) {
    const props = apiDatavizToEvervizChartProps(
      { ...contentBlock.inlineChart, headline: contentBlock.headline },
      contentBlock.blockId
    );
    return (
      <LandingPageContent
        maxWidth={maxContentWidth}
        className="mb-xl mt-l text-body-large leading-body"
      >
        <div ref={datavizContainerRef}>
          <EvervizChart {...props} />
        </div>
      </LandingPageContent>
    );
  }

  if (isDatavizBlockContentBlock(contentBlock)) {
    const props = apiChartToEvervizChartProps({
      headline: contentBlock.headline,
      headlineId: contentBlock.blockId,
      chart: contentBlock.datavizBlock,
    });
    return (
      <LandingPageContent
        maxWidth={maxContentWidth}
        className="mb-xl mt-l text-body-large leading-body"
      >
        <div ref={datavizContainerRef}>
          <EvervizChart {...props} />
        </div>
      </LandingPageContent>
    );
  }

  if (isPlainContentBlockContentBlock(contentBlock)) {
    return (
      <LandingPageContent
        maxWidth={maxContentWidth}
        className="text-body-large leading-body"
      >
        <div ref={textContainerRef}>
          <PlainContentBlock contentBlock={contentBlock} />
        </div>
      </LandingPageContent>
    );
  }

  if (isCorrectionBlockContentBlock(contentBlock)) {
    return (
      <LandingPageContent
        maxWidth={maxContentWidth}
        className="mb-l mt-xl text-body-large leading-body"
      >
        <div ref={textContainerRef}>
          <CorrectionBlockCard
            title={contentBlock?.headline}
            bodyHTML={contentBlock.correctionText}
            blockId={contentBlock.blockId}
          />
        </div>
      </LandingPageContent>
    );
  }

  if (isTabbedChartContentBlock(contentBlock)) {
    const { headline, subhead, title, tabs } = contentBlock;

    const selectOptions = tabs.map<OptionData>(({ tab }, idx) => ({
      id: tab.title,
      label: tab.title,
      index: idx,
    }));

    const tabsProps = tabs
      .map<TabbedChartTab | null>(({ tab }) =>
        tab.inlineChart
          ? {
              label: tab.title,
              tabContent: (
                <EvervizChart {...tab.inlineChart} showHeader={false} />
              ),
            }
          : null
      )
      .filter(Boolean) as TabbedChartTab[];

    return (
      <LandingPageContent
        maxWidth={maxContentWidth}
        key={`${contentBlock.blockId}`}
      >
        <div ref={tabbedChartContaineref}>
          <TabbedCharts
            title={title}
            headline={headline}
            subhead={subhead}
            options={selectOptions}
            tabs={tabsProps}
            blockId={contentBlock.blockId}
          />
        </div>
      </LandingPageContent>
    );
  }

  return null;
};
